import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges
} from '@angular/core';
import { AsyncPipe, NgIf } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  combineLatest,
  EMPTY,
  map,
  Observable,
  retry,
  Subscription,
  throwError,
  timer
} from 'rxjs';
import { LoadingShimmerModule } from 'loading-shimmer';
import { FileUtils } from '../../utils/file-utils';

/**
 * The action shown alongside Preview:
 * - 'download' - the file has been saved to the backend and can be downloaded
 * - 'remove' - the file is staged locally and has not been saved yet
 * - 'none' - the file can only be previewed (e.g. read-only contexts)
 */
export type FileListItemAction = 'download' | 'remove' | 'none';

const PREVIEW_AVAILABILITY_MAX_RETRIES = 9;
const PREVIEW_AVAILABILITY_MAX_DELAY_MS = 60 * 1000;
const PREVIEW_AVAILABILITY_BASE_DELAY_MS = 3000;

/** Shown in place of the thumbnail image if it fails to load, or the file never becomes available. */
export const FILE_LIST_ITEM_THUMBNAIL_FALLBACK_SRC = 'assets/placeholder/no-image.svg';

@Component({
  selector: 'app-file-list-item',
  templateUrl: './file-list-item.component.html',
  styleUrls: ['./file-list-item.component.scss'],
  standalone: true,
  imports: [AsyncPipe, NgIf, LoadingShimmerModule],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FileListItemComponent implements OnChanges {
  @Input() fileName = '';
  @Input() fileSizeInBytes: number | null = null;
  @Input() action: FileListItemAction = 'none';
  @Input() disabled = false;
  @Input() hasError = false;
  @Input() previewLabel: string = $localize`Preview`;
  @Input() showPreview = true;
  /** Image src for the thumbnail preview. Omit for non-image files - no fallback icon is shown. */
  @Input() thumbnailUrl: string | null = null;
  /**
   * When provided, the Preview button (and thumbnail, if any) stay disabled/shimmering until this
   * URL is confirmed available, polling with backoff. Needed because freshly uploaded attachments
   * can 404 briefly before they finish propagating. Omit for files that are always immediately
   * available (e.g. a local, not-yet-uploaded File) - Preview/thumbnail will be enabled right away.
   *
   * Also drives the Preview button's click behavior: when set, clicking Preview opens this URL in
   * a new tab directly - the component has everything it needs, so there's no reason to make every
   * consumer re-implement the same window.open. Omit (and handle previewClicked yourself) when
   * there's no static URL to open, e.g. a local File that must be read into a blob URL first.
   */
  @Input() previewUrl: string | null = null;

  @Output() previewClicked = new EventEmitter<void>();
  @Output() downloadClicked = new EventEmitter<void>();
  @Output() removeClicked = new EventEmitter<void>();

  private readonly http = inject(HttpClient);
  private readonly destroyRef = inject(DestroyRef);

  public readonly thumbnailFallbackSrc = FILE_LIST_ITEM_THUMBNAIL_FALLBACK_SRC;

  private readonly _fileSizeString = new BehaviorSubject<string>('');
  public readonly fileSizeString$ = this._fileSizeString.asObservable();

  // The file name split around the last '.' so the base name can truncate with an ellipsis while
  // the extension always stays visible.
  private readonly _fileNameBase = new BehaviorSubject<string>('');
  public readonly fileNameBase$ = this._fileNameBase.asObservable();

  private readonly _fileNameExtension = new BehaviorSubject<string>('');
  public readonly fileNameExtension$ = this._fileNameExtension.asObservable();

  private readonly _thumbnailUrl = new BehaviorSubject<string | null>(null);
  public readonly thumbnailUrl$: Observable<string | null> = this._thumbnailUrl.asObservable();

  private readonly _thumbnailBroken = new BehaviorSubject<boolean>(false);
  private readonly thumbnailBroken$: Observable<boolean> = this._thumbnailBroken.asObservable();

  private readonly _closeIconSrc = new BehaviorSubject<string>('assets/icons/blue/X.svg');
  public readonly closeIconSrc$ = this._closeIconSrc.asObservable();

  private readonly _previewAvailable = new BehaviorSubject<boolean>(true);
  public readonly previewAvailable$: Observable<boolean> = this._previewAvailable.asObservable();

  // Distinct from "not yet available" - set when polling gives up entirely (retries exhausted, or
  // a non-retryable error), so the thumbnail can stop shimmering and show the fallback instead of
  // spinning forever.
  private readonly _previewUnavailable = new BehaviorSubject<boolean>(false);
  private readonly previewUnavailable$: Observable<boolean> = this._previewUnavailable.asObservable();

  private readonly showThumbnailFallbackState$: Observable<boolean> = combineLatest([
    this.thumbnailBroken$,
    this.previewUnavailable$
  ]).pipe(map(([broken, unavailable]) => broken || unavailable));

  public readonly showThumbnailBox$: Observable<boolean> = this.thumbnailUrl$.pipe(map(url => !!url));

  public readonly showThumbnailShimmer$: Observable<boolean> = combineLatest([
    this.showThumbnailBox$,
    this.previewAvailable$,
    this.showThumbnailFallbackState$
  ]).pipe(map(([showBox, available, fallback]) => showBox && !available && !fallback));

  public readonly showThumbnailImage$: Observable<boolean> = combineLatest([
    this.showThumbnailBox$,
    this.previewAvailable$,
    this.showThumbnailFallbackState$
  ]).pipe(map(([showBox, available, fallback]) => showBox && available && !fallback));

  public readonly showThumbnailFallback$: Observable<boolean> = combineLatest([
    this.showThumbnailBox$,
    this.showThumbnailFallbackState$
  ]).pipe(map(([showBox, fallback]) => showBox && fallback));

  private pollSubscription: Subscription | null = null;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.fileName) {
      const extensionIndex = this.fileName.lastIndexOf('.');
      const hasExtension = extensionIndex > 0;
      this._fileNameBase.next(hasExtension ? this.fileName.slice(0, extensionIndex) : this.fileName);
      this._fileNameExtension.next(hasExtension ? this.fileName.slice(extensionIndex) : '');
    }
    if (changes.fileSizeInBytes) {
      const sizeString = this.fileSizeInBytes !== null ? FileUtils.getFileSizeString(this.fileSizeInBytes) : '';
      this._fileSizeString.next(sizeString);
    }
    if (changes.hasError) {
      this._closeIconSrc.next(this.hasError ? 'assets/icons/red/X.svg' : 'assets/icons/blue/X.svg');
    }
    if (changes.thumbnailUrl) {
      this._thumbnailBroken.next(false);
      this._thumbnailUrl.next(this.thumbnailUrl);
    }
    if (changes.previewUrl) {
      this._previewUnavailable.next(false);
      this.pollForPreviewAvailability();
    }
  }

  public onThumbnailError(): void {
    this._thumbnailBroken.next(true);
  }

  public onPreviewClick(): void {
    if (this.previewUrl) {
      window.open(this.previewUrl, '_blank', 'noopener,noreferrer');
    }
    this.previewClicked.emit();
  }

  private pollForPreviewAvailability(): void {
    this.pollSubscription?.unsubscribe();

    if (!this.previewUrl) {
      this._previewAvailable.next(true);
      return;
    }

    this._previewAvailable.next(false);

    this.pollSubscription = this.http
      .get(this.previewUrl, { responseType: 'blob', headers: { Range: 'bytes=0-0' } })
      .pipe(
        retry({
          delay: (error: HttpErrorResponse, retryCount) => {
            // S3 responds with 403 (instead of 404) for objects that don't exist yet
            const notYetAvailable = error.status === 404 || error.status === 403;
            if (notYetAvailable && retryCount < PREVIEW_AVAILABILITY_MAX_RETRIES) {
              const queuedDelay = PREVIEW_AVAILABILITY_BASE_DELAY_MS * Math.pow(3, retryCount);
              const backoffDelay = Math.min(queuedDelay, PREVIEW_AVAILABILITY_MAX_DELAY_MS);
              return timer(backoffDelay);
            }
            return throwError(() => error);
          }
        }),
        // If the file never becomes available (or retries are exhausted), fail closed rather than
        // throwing an unhandled error - Preview stays disabled and the thumbnail (if any) falls
        // back to the placeholder image instead of shimmering forever.
        catchError(() => {
          this._previewUnavailable.next(true);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this._previewAvailable.next(true));
  }
}
